import React, { useState, useEffect, useRef } from 'react';
import { GetRequest } from 'components/common';
import { NumberWithUnitInput } from 'components/common';
import styles from 'components/show_drawing/redraw_form/_style.module.scss';
import MyEventListener from 'components/common/MyEventListener.jsx';
import Constants from 'components/show_drawing/Constants.jsx';
import OptionRowFloatInfr from 'components/layout/options/OptionRowFloatInfr.jsx';

const PartMaterialsChooser = ({ id, unit, register, errors }) => {
  const custom = 'custom';
  const [partMaterials, setPartMaterials] = useState([]);
  const [selectedValues, setSelectedValues] = useState({});
  const [askCustomMaterial, setAskCustomMaterial] = useState(false);
  const [customThicknesses, setCustomThicknesses] = useState({});
  const labelRefs = useRef({});
  const previousValuesRef = useRef({});

  const defaultCustomThickness = () => {
    if (unit === 'in') { return 0.0394; }
    if (unit === 'infr') { return "0+1/4"; }
    return 1;
  };

  const fetchMaterials = () => {
    GetRequest({
      url: `/materials/${window.locale}/${id}/${unit}`,
      callback: (data) => {
        if (data.part_materials && data.part_materials.length > 0) {
          setPartMaterials(data.part_materials);
          setAskCustomMaterial((_prev) => data.ask_custom_material);

          const initialValues = {};
          data.part_materials.forEach((list) => {
            if (list.default_material_id) {
              initialValues[list.list_number] = list.default_material_id;
            } else if (list.materials && list.materials.length > 0) {
              initialValues[list.list_number] = list.materials[0].id;
            }
          });
          setSelectedValues(initialValues);
        }
      }
    });
  };

  const onChange = (listNumber, value) => {
    setSelectedValues((prev) => ({
      ...prev,
      [listNumber]: value
    }));

    if (value === custom && customThicknesses[listNumber] === undefined) {
      setCustomThicknesses((prev) => ({
        ...prev,
        [listNumber]: defaultCustomThickness()
      }));
    }
  };

  const onCustomThicknessChange = (listNumber, event) => {
    setCustomThicknesses((prev) => ({
      ...prev,
      [listNumber]: event.target.value
    }));
  };

  const onDrawingOptionValueSetMsg = (msg) => {
    if (msg.detail.id !== id) { return; }

    const name = msg.detail.name;
    if (name && name.startsWith('part_material_')) {
      const listNumber = parseInt(name.replace('part_material_', ''), 10);
      emitEvent('addDebugEntry', { event: "[PartMaterialsChooser] value set from localStorage", name: name, value: msg.detail.value });

      const value = msg.detail.value;
      const valueParts = String(value).split('|');
      if (valueParts.length === 2 && valueParts[0] === custom) {
        setSelectedValues((prev) => ({
          ...prev,
          [listNumber]: custom
        }));
        setCustomThicknesses((prev) => ({
          ...prev,
          [listNumber]: valueParts[1]
        }));
      } else {
        setSelectedValues((prev) => ({
          ...prev,
          [listNumber]: value
        }));
      }
    }

  };

  const emitLabelWidths = () => {
    // Apply max width to our labels and emit
    Object.keys(labelRefs.current).forEach((listNumber) => {
      const element = labelRefs.current[listNumber];
      if (element) {
        const myWidth = $(element).width();
        emitEvent(Constants.drawing_input_label_width, { id: id, name: `part_material_${listNumber}`, width: myWidth, group: 'dropdowns' });
      }
    });
  };

  const adjustLabelWidthMsg = (e) => {
    if (e.detail.id == id && e.detail.group == 'dropdowns') {
      Object.keys(labelRefs.current).forEach((listNumber) => {
        const element = labelRefs.current[listNumber];
        if (element && e.detail.name != `part_material_${listNumber}`) {
          let myWidth = $(element).width();
          let othersWidth = e.detail.width;
          if (othersWidth > myWidth) {
            $(element).width(othersWidth);
          }
        }
      });
    }
  };

  const emitChangeCompleted = () => {
    const previousValues = previousValuesRef.current;

    Object.keys(selectedValues).forEach((listNumber) => {
      // Only emit if this is a new key or the value actually changed
      if (previousValues[listNumber] !== selectedValues[listNumber]) {
        emitEvent(Constants.drawing_option_change_completed, { id: id, name: `part_material_${listNumber}` });
      }
    });

    previousValuesRef.current = { ...selectedValues };
  };

  useEffect(fetchMaterials, [id, unit]);
  useEffect(emitLabelWidths, [partMaterials]);
  useEffect(emitChangeCompleted, [selectedValues]);
  useEffect(() => {
    setCustomThicknesses((prev) => {
      const updated = {};
      Object.keys(prev).forEach((listNumber) => {
        updated[listNumber] = defaultCustomThickness();
      });
      return updated;
    });
  }, [unit]);

  MyEventListener([
    {
      key: Constants.drawing_input_label_width,
      callback: adjustLabelWidthMsg
    },
    {
      key: Constants.drawing_option_set_value,
      callback: onDrawingOptionValueSetMsg
    }
  ]);

  if (partMaterials.length === 0) {
    return null;
  }

  return (
    <>
      {partMaterials.map((list) => (
        <div key={list.list_number} className={styles.row}>
          <div className="input-group" style={{ width: '100%' }}>
            <span
              className={`d_input_add_on input-group-addon input-group-addon-${window.locale} ${styles.input_add_on}`}
              ref={(el) => { labelRefs.current[list.list_number] = el; }}
            >
              {list.name || `Part Material ${list.list_number}`}
            </span>
            <select
              name={`part_material_${list.list_number}`}
              value={selectedValues[list.list_number] || ''}
              onChange={(e) => onChange(list.list_number, e.target.value)}
              className="form-control"
            >
              {list.materials.map((material) => (
                <option key={material.id} value={material.id}>
                  {material.name}
                </option>
              ))}
              {askCustomMaterial && (
                <option value={custom}> { t('drawing.enter_custom_material') } </option>
              )}
            </select>
          </div>

          {selectedValues[list.list_number] === custom && (
            <div style={{ marginTop: "8px" }}>
              {unit === "infr" && <OptionRowFloatInfr
                item={{}}
                description=""
                name={`custom_part_material_value_${list.list_number}`}
                value={customThicknesses[list.list_number] !== undefined ? customThicknesses[list.list_number] : defaultCustomThickness()}
                unit={unit}
                abc_line=""
                onChange={(e) => onCustomThicknessChange(list.list_number, e)}
                onKeyUp={() => {}}
                wide={false}
                doNotLoadFromLocalStorage={true}
                disabled={false}
                decreaseNum={() => {}}
              />}

              {unit !== "infr" && <NumberWithUnitInput
                name={`custom_part_material_value_${list.list_number}`}
                unit={unit}
                placeholder=""
                register={register}
                class_name="bla"
                errors={errors}
                validators={[]}
                value={customThicknesses[list.list_number] !== undefined ? customThicknesses[list.list_number] : defaultCustomThickness()}
                onChange={(e) => onCustomThicknessChange(list.list_number, e)}
              />}
            </div>
          )}
        </div>
      ))}
    </>
  );
};

export default PartMaterialsChooser;
